import { useCart } from "../context/CartContext";
import { imgUrl, money, SWATCH, api } from "../lib/api";
import { X, Plus, Minus, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        items: items.map((i) => ({
          product_id: i.productId,
          qty: i.qty,
          metal: i.metal || "silver",
          carat: i.carat || 1,
          color: i.color || "",
          ring_size: i.ringSize || null,
          band_width: i.bandWidth || null,
        })),
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data?.url) {
        // Preserve session id so success page can read it if Stripe doesn't roundtrip it
        try { sessionStorage.setItem("dreamonds_last_session", data.session_id); } catch {}
        window.location.href = data.url;
      } else {
        toast("Could not start checkout. Please try again.");
        setBusy(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Please try again.";
      toast(String(msg));
      setBusy(false);
    }
  };

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[440px] bg-[#FFFFFF] border-l border-[#E5E5E5] transition-transform duration-500 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-7 h-20 border-b border-[#E5E5E5]">
          <span className="label-track text-[#0A0A0A]">Your Selection</span>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-[#737373] hover:text-[#0A0A0A]">
            <X size={20} />
          </button>
        </div>

        <div className="h-[calc(100%-14rem-5rem)] overflow-y-auto px-7 py-6 space-y-6">
          {items.length === 0 && (
            <p className="text-[#737373] text-sm mt-10 text-center">Your selection is empty.</p>
          )}
          {items.map((i) => (
            <div key={i.lineId} data-testid={`cart-item-${i.lineId}`} className="flex gap-4">
              <div className="w-20 h-24 bg-[#F7F7F9] overflow-hidden flex-shrink-0">
                {i.image && <img src={imgUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <h4 className="font-heading text-base text-[#0A0A0A]">{i.name}</h4>
                  <button onClick={() => removeItem(i.lineId)} data-testid={`cart-remove-${i.lineId}`} className="text-[#737373] hover:text-[#0A0A0A]">
                    <X size={15} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: SWATCH[i.color] }} />
                  <span className="text-xs text-[#737373]">{i.color}</span>
                  {i.metalLabel && (
                    <span className="text-xs text-[#737373]">· {i.metalLabel} · {i.carat}ct {i.shape || ""}</span>
                  )}
                  {i.ringSize && (
                    <span className="text-xs text-[#737373]">· Size {i.ringSize}</span>
                  )}
                  {i.bandWidth && (
                    <span className="text-xs text-[#737373]">· {i.bandWidth}mm</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-[#E5E5E5]">
                    <button onClick={() => updateQty(i.lineId, i.qty - 1)} className="px-2 py-1 text-[#737373] hover:text-[#0A0A0A]"><Minus size={13} /></button>
                    <span className="px-3 text-sm text-[#0A0A0A]">{i.qty}</span>
                    <button onClick={() => updateQty(i.lineId, i.qty + 1)} className="px-2 py-1 text-[#737373] hover:text-[#0A0A0A]"><Plus size={13} /></button>
                  </div>
                  <span className="text-sm text-[#0A0A0A]">{money(i.price * i.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute bottom-0 inset-x-0 border-t border-[#E5E5E5] px-7 py-6 bg-[#FFFFFF]">
          <div className="flex justify-between mb-3">
            <span className="label-track text-[#737373]">Subtotal</span>
            <span data-testid="cart-total" className="text-[#0A0A0A]">{money(total)}</span>
          </div>
          <div className="text-[10px] text-[#525252] leading-relaxed mb-4 space-y-0.5 border-t border-[#EFE7D8] pt-3">
            <p>· <span className="text-[#0A0A0A]">3-week</span> handcraft &amp; insured shipping worldwide</p>
            <p>· <span className="text-[#0A0A0A]">30-day</span> easy returns for unworn pieces</p>
            <p>· CAD · secure Stripe checkout</p>
          </div>
          <button
            data-testid="checkout-button"
            disabled={items.length === 0 || busy}
            onClick={checkout}
            className="w-full bg-[#0A0A0A] text-white py-4 label-track transition-all duration-300 hover:bg-white hover:text-[#0A0A0A] hover:shadow-[inset_0_0_0_1px_#0A0A0A] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <><Lock size={13} /> Proceed to Checkout</>}
          </button>
        </div>
      </aside>
    </>
  );
};
